import React from 'react';
import { FiChevronDown, FiSettings } from 'react-icons/fi';
import { C } from '../../utils/constants';
import { cn } from '../../lib/utils';
import { Card as UICard, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge as UIBadge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';

const cx = cn;

// ─── color token mapping ─────────────────────────────────────────────
// Every AI Gateway page passes hex colors (from utils/constants `C`) into
// these primitives. Map those hex values onto the product's semantic
// Tailwind palette so the same primitive renders the same way everywhere
// in the app. Only true third-party brand colors (OpenAI/Anthropic/
// Google/AWS) fall back to literal hex, since there's no product token
// for them.
const COLOR_KEY = {
  [C.green]: 'success',
  [C.red]: 'destructive',
  [C.amber]: 'warning',
  [C.orange]: 'primary',
  [C.teal]: 'teal',
  [C.cyan]: 'cyan',
  [C.purple]: 'purple',
  [C.white]: 'white',
  [C.muted]: 'muted',
  [C.dim]: 'dim',
  '#10a37f': 'openai',
  '#cc774e': 'anthropic',
  '#4285f4': 'google',
  '#ff9900': 'aws',
};

const TEXT_CLASS = {
  success: 'text-green-400',
  destructive: 'text-red-400',
  warning: 'text-yellow-400',
  primary: 'text-primary',
  teal: 'text-teal-400',
  cyan: 'text-cyan-400',
  purple: 'text-purple-400',
  white: 'text-[var(--c-foreground-ffffff)]',
  muted: 'text-[var(--c-foreground-9ca3af)]',
  dim: 'text-[var(--c-foreground-6b7280)]',
  openai: 'text-[var(--c-accent-10a37f)]',
  anthropic: 'text-[var(--c-accent-cc774e)]',
  google: 'text-[var(--c-accent-4285f4)]',
  aws: 'text-[var(--c-accent-ff9900)]',
};

const DOT_CLASS = {
  success: 'bg-green-400',
  destructive: 'bg-red-400',
  warning: 'bg-yellow-400',
  primary: 'bg-primary',
  teal: 'bg-teal-400',
  cyan: 'bg-cyan-400',
  purple: 'bg-purple-400',
  white: 'bg-[var(--c-surface-ffffff)]',
  muted: 'bg-[var(--c-surface-9ca3af)]',
  dim: 'bg-[var(--c-surface-6b7280)]',
  openai: 'bg-[var(--c-accent-10a37f)]',
  anthropic: 'bg-[var(--c-accent-cc774e)]',
  google: 'bg-[var(--c-accent-4285f4)]',
  aws: 'bg-[var(--c-accent-ff9900)]',
};

const SOFT_CLASS = {
  success: 'border-green-500/25 bg-green-500/15 text-green-400',
  destructive: 'border-red-500/25 bg-red-500/15 text-red-400',
  warning: 'border-yellow-500/25 bg-yellow-500/15 text-yellow-400',
  primary: 'border-primary/25 bg-primary/15 text-primary',
  teal: 'border-teal-500/25 bg-teal-500/15 text-teal-400',
  cyan: 'border-cyan-500/25 bg-cyan-500/15 text-cyan-400',
  purple: 'border-purple-500/25 bg-purple-500/15 text-purple-400',
  white: 'border-[color-mix(in_srgb,var(--c-border-ffffff)_20%,transparent)] bg-[color-mix(in_srgb,var(--c-surface-ffffff)_10%,transparent)] text-[var(--c-foreground-ffffff)]',
  muted: 'border-[color-mix(in_srgb,var(--c-border-6b7280)_25%,transparent)] bg-[color-mix(in_srgb,var(--c-surface-6b7280)_15%,transparent)] text-[var(--c-foreground-9ca3af)]',
  dim: 'border-[color-mix(in_srgb,var(--c-border-4b5563)_25%,transparent)] bg-[color-mix(in_srgb,var(--c-surface-4b5563)_15%,transparent)] text-[var(--c-foreground-6b7280)]',
  openai: 'border-[color-mix(in_srgb,var(--c-accent-10a37f)_25%,transparent)] bg-[color-mix(in_srgb,var(--c-accent-10a37f)_15%,transparent)] text-[var(--c-accent-10a37f)]',
  anthropic: 'border-[color-mix(in_srgb,var(--c-accent-cc774e)_25%,transparent)] bg-[color-mix(in_srgb,var(--c-accent-cc774e)_15%,transparent)] text-[var(--c-accent-cc774e)]',
  google: 'border-[color-mix(in_srgb,var(--c-accent-4285f4)_25%,transparent)] bg-[color-mix(in_srgb,var(--c-accent-4285f4)_15%,transparent)] text-[var(--c-accent-4285f4)]',
  aws: 'border-[color-mix(in_srgb,var(--c-accent-ff9900)_25%,transparent)] bg-[color-mix(in_srgb,var(--c-accent-ff9900)_15%,transparent)] text-[var(--c-accent-ff9900)]',
};

// Only variants backed by tokens that are actually defined in
// tailwind.config.js (`--success`, `--destructive`) render correctly.
// `badge.jsx`'s "warning" and "default" variants reference
// `warning`/`primary-light` tokens that don't exist, so those colors
// are handled via the SOFT_CLASS outline fallback below instead.
const BADGE_VARIANT = {
  success: 'success',
  destructive: 'destructive',
};

function keyFor(color) {
  return COLOR_KEY[color] || 'dim';
}

// Kept for pages that call tone(color).dot / .soft / .glow directly.
const tone = (color) => {
  const key = keyFor(color);
  return {
    badge: SOFT_CLASS[key],
    dot: DOT_CLASS[key],
    soft: SOFT_CLASS[key],
    glow: cx(SOFT_CLASS[key], 'shadow-[0_0_20px_-4px_var(--tw-shadow-color)]'),
  };
};

const textTone = (color) => TEXT_CLASS[keyFor(color)] || 'text-[var(--c-foreground-ffffff)]';

// ─── primitives (built on src/components/ui) ─────────────────────────

const Badge = ({ c = C.teal, children, className = '' }) => {
  const key = keyFor(c);
  const variant = BADGE_VARIANT[key];

  if (variant) {
    return (
      <UIBadge variant={variant} className={className}>
        {children}
      </UIBadge>
    );
  }

  return (
    <UIBadge variant="outline" className={cx(SOFT_CLASS[key], className)}>
      {children}
    </UIBadge>
  );
};

const GLOW_SHADOW = {
  success: 'shadow-soft',
  destructive: 'shadow-soft',
  warning: 'shadow-soft',
  primary: 'shadow-soft',
  teal: 'shadow-soft',
};

// `accent`/`hoverLift` are opt-in extras used by the newer, richer pages
// (LLM Routes, Security Center) — omitted everywhere else, so existing
// callers render byte-for-byte the same as before.
const Card = ({ children, glow, accent, hoverLift, className = '', style }) => (
  <UICard
    className={cx(
      'p-5 font-body',
      accent && 'relative overflow-hidden',
      glow && cx('border-current/30', GLOW_SHADOW[keyFor(glow)]),
      hoverLift && 'transition-all duration-200 hover:-translate-y-px hover:border-[color-mix(in_srgb,var(--c-border-ffffff)_15%,transparent)] hover:shadow-soft-lg',
      className
    )}
    style={style}
  >
    {accent && (
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
      />
    )}
    {children}
  </UICard>
);

const btnVariantProps = {
  primary: { variant: 'default' },
  gradient: { variant: 'gradient', className: 'shadow-soft' },
  teal: { variant: 'default', className: 'bg-teal-500 text-[var(--c-foreground-ffffff)] hover:bg-teal-600' },
  ghost: { variant: 'outline' },
  danger: { variant: 'destructive' },
  green: { variant: 'outline', className: 'border-green-500/30 bg-green-500/10 text-green-400 hover:bg-green-500/20' },
  purple: { variant: 'outline', className: 'border-purple-500/30 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20' },
};

const Btn = ({ children, onClick, v = 'primary', sm, disabled, className = '' }) => {
  const preset = btnVariantProps[v] || btnVariantProps.primary;

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant={preset.variant}
      size={sm ? 'sm' : 'default'}
      className={cx(preset.className, className)}
    >
      {children}
    </Button>
  );
};

const Toggle = ({ on, onChange, size }) => (
  <Switch checked={!!on} onCheckedChange={onChange} size={size} />
);

// `cozy` opts into slightly larger, higher-contrast type for pages whose
// stat cards carry denser sub-text (e.g. Enrichment's profile counts) —
// default sizing is untouched so every other page using Stat is unaffected.
const Stat = ({ label, val, sub, col = C.white, icon, cozy = false }) => (
  <Card>
    <div className="flex items-start justify-between">
      <div>
        <div className={cx('mb-1.5 uppercase tracking-[0.1em] text-[var(--c-foreground-9ca3af)]', cozy ? 'text-[11px]' : 'text-[10px]')}>{label}</div>
        <div className="font-body text-2xl font-extrabold leading-none text-[var(--c-foreground-ffffff)]">{val}</div>
        {sub && <div className={cx('mt-1', cozy ? 'text-[11.5px] text-[var(--c-foreground-94a3b8)]' : 'text-[10px] text-[var(--c-foreground-6b7280)]')}>{sub}</div>}
      </div>
      {icon && (
        <div className={cx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', tone(col).soft)}>
          {icon}
        </div>
      )}
    </div>
  </Card>
);

// `icon`/`accent` are opt-in — pages that don't pass an icon get the exact
// same markup as before (single-child flex wrapper has no visual effect).
const SectionHead = ({ title, sub, action, icon: Icon, accent }) => (
  <div className="mb-[18px] flex items-start justify-between">
    <div className="flex items-start gap-3">
      {Icon && (
        <div
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border"
          style={{ backgroundColor: `${accent}1A`, borderColor: `${accent}40`, color: accent }}
        >
          <Icon className="h-[18px] w-[18px]" />
        </div>
      )}
      <div>
        <div className="text-2xl font-bold text-[var(--c-foreground-ffffff)]">{title}</div>
        {sub && <div className="mt-1 text-sm leading-5 text-[var(--c-foreground-9ca3af)]">{sub}</div>}
      </div>
    </div>
    {action}
  </div>
);

const Tabs = ({ tabs, active, onChange, size = 'sm' }) => (
  <div className="mb-4 flex flex-wrap gap-1.5">
    {tabs.map(t => (
      <button
        key={t}
        onClick={() => onChange(t)}
        className={cx(
          'rounded-lg font-body font-semibold transition-all duration-200',
          size === 'md' ? 'px-4 py-2.5 text-base' : 'px-3.5 py-1.5 text-[11px]',
          active === t ? 'bg-primary text-[var(--c-foreground-ffffff)]' : 'bg-dark-700/50 text-[var(--c-foreground-9ca3af)] hover:bg-dark-700 hover:text-[var(--c-foreground-e5e7eb)]'
        )}
      >
        {t}
      </button>
    ))}
  </div>
);

const Modal = ({ title, onClose, footer, children }) => (
  <Dialog open onOpenChange={(next) => { if (!next) onClose?.(); }}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <DialogBody>{children}</DialogBody>
      {footer && <DialogFooter>{footer}</DialogFooter>}
    </DialogContent>
  </Dialog>
);

const Inp = ({ label, value, onChange, placeholder, type = 'text', required, disabled }) => (
  <div className="mb-[18px]">
    {label && (
      <Label className="mb-1.5 block text-[var(--c-foreground-ffffff)]">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
    )}
    <Input
      type={type}
      value={value}
      onChange={e => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    />
  </div>
);

const selectClassName = cn(
  'h-11 w-full cursor-pointer rounded-lg px-3 text-sm text-[var(--c-foreground-ffffff)] outline-none transition-all',
  'focus:outline-none focus:ring-2 focus:ring-primary/30'
);

const Sl = ({ label, value, onChange, opts, required }) => (
  <div className="mb-[18px]">
    {label && (
      <Label className="mb-1.5 block text-[var(--c-foreground-ffffff)]">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
    )}
    <select
      value={value}
      onChange={e => onChange && onChange(e.target.value)}
      className={selectClassName}
      style={{ backgroundColor: 'color-mix(in srgb, var(--c-surface-0f172a) 50%, transparent)', borderColor: 'var(--c-border-232942)', borderWidth: '1px' }}
    >
      {opts.map(o => (
        <option key={o.v || o} value={o.v || o}>
          {o.l || o}
        </option>
      ))}
    </select>
  </div>
);

const ApigeeRef = ({ method, path }) => (
  <div className={cx('mt-2.5 rounded-lg border px-3 py-2', SOFT_CLASS.teal)}>
    <div className="mb-[3px] text-[8px] font-bold tracking-[0.1em] text-teal-400">FORGESPHERE X POLICY CALL</div>
    <div className="font-body text-[10px] text-[var(--c-foreground-9ca3af)]">{method} {path}</div>
  </div>
);

const pdC = {
  OpenAI: '#10a37f',
  Anthropic: '#cc774e',
  Google: '#4285f4',
  AWS: '#ff9900',
  Custom: C.purple,
};

const PDot = ({ p }) => (
  <span className={cx('mr-[5px] inline-block h-[7px] w-[7px] rounded-full', tone(pdC[p] || C.muted).dot)} />
);

const THead = ({ cols }) => (
  <thead>
    <tr className="border-b border-border">
      {cols.map(c => (
        <th key={c} className="px-2.5 py-[7px] text-left text-[9px] font-bold uppercase tracking-[0.1em] text-[var(--c-foreground-9ca3af)]">{c}</th>
      ))}
    </tr>
  </thead>
);

const CSTYLE = {
  tt: { background: '#15192b', border: '1px solid #343b5c', borderRadius: 8, fontSize: 11 },
  grid: { stroke: 'var(--c-foreground-343b5c)' },
  tick: { fill: 'var(--c-foreground-94a3b8)', fontSize: 9 },
};

const sevC = {
  critical: C.red,
  high: C.orange,
  medium: C.amber,
  low: C.teal,
};

const ConfigDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const options = [
    { v: 'git', l: 'Git Configuration' },
    { v: 'gcs', l: 'Google Cloud Storage' },
    { v: 'standard', l: 'AI Proxy Standard Linting' },
    { v: 'custom', l: 'AI Proxy Custom Linting' },
  ];

  const select = (v) => {
    onChange(v);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block">
      <Button
        type="button"
        variant={value ? 'default' : 'outline'}
        size="sm"
        className="gap-1.5"
        onClick={() => setIsOpen(open => !open)}
      >
        <FiSettings className="h-[13px] w-[13px]" />
        <span>Config</span>
        <FiChevronDown className={cx('h-3 w-3 transition-transform duration-200', isOpen && 'rotate-180')} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-[110%] z-[100] min-w-[200px] rounded-xl border border-border bg-background-card p-1.5 shadow-soft-lg">
          {options.map((opt) => (
            <button
              key={opt.v}
              onClick={() => select(opt.v)}
              className={cx(
                'flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-[var(--c-foreground-d1d5db)] transition-all duration-150 hover:bg-[color-mix(in_srgb,var(--c-surface-ffffff)_5%,transparent)] hover:text-[var(--c-foreground-ffffff)]',
                value === opt.v && 'bg-[color-mix(in_srgb,var(--c-surface-ffffff)_10%,transparent)] text-[var(--c-foreground-ffffff)]'
              )}
            >
              <FiSettings className="h-[13px] w-[13px] opacity-60" />
              {opt.l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConfigDropdown;

export {
  Badge,
  Card,
  CardContent,
  Stat,
  Btn,
  Toggle,
  SectionHead,
  Tabs,
  Modal,
  Inp,
  Sl,
  ApigeeRef,
  PDot,
  THead,
  CSTYLE,
  sevC,
  pdC,
  ConfigDropdown,
  cx,
  tone,
  textTone,
};
